import { useEffect, useState } from 'react';

import {
  type PulseFragment,
  type PulseQuery,
  type PulseQueryVariables,
  PulseV1PulseView,
  usePulseQuery,
  withStandardGetHook,
} from '@spotify/confidence-graphql';

import type { PulseBlock } from '../types';

export const PULSE_POLL_INTERVAL_MS = 5000;
export const PULSE_POLL_TIMEOUT_MS = 390000;

type PulseQueryOptions = Parameters<typeof usePulseQuery>[0] & {
  variables: PulseQueryVariables;
};

const useBasicPulseQuery = (options: PulseQueryOptions) => {
  const basicOptions: Parameters<typeof usePulseQuery>[0] = {
    ...options,
    variables: {
      ...options.variables,
      view: PulseV1PulseView.PulseViewBasic,
    },
  };

  return usePulseQuery(basicOptions);
};

const usePulseImpl = withStandardGetHook<
  PulseQuery,
  PulseFragment,
  'pulse',
  PulseQueryVariables
>(useBasicPulseQuery, 'pulse');

export type UsePulseOptions = Parameters<typeof usePulseImpl>[0];
export type UsePulseResult = ReturnType<typeof usePulseImpl> & {
  blocks: PulseBlock[];
  isPollingTimedOut: boolean;
};

type PulseResourceOptions = {
  keepPolling?: boolean;
};

export const usePulseResource = (
  options: UsePulseOptions,
  { keepPolling = false }: PulseResourceOptions = {},
): UsePulseResult => {
  const result = usePulseImpl(options);
  const { startPolling, stopPolling } = result;
  const blocks = result.pulse?.blocks ?? [];
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);

  useEffect(() => {
    if (!keepPolling) {
      setIsPollingTimedOut(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsPollingTimedOut(true);
    }, PULSE_POLL_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [keepPolling]);

  useEffect(() => {
    if (!keepPolling || isPollingTimedOut) {
      stopPolling();
      return undefined;
    }

    startPolling(PULSE_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [isPollingTimedOut, keepPolling, startPolling, stopPolling]);

  return {
    ...result,
    blocks,
    isPollingTimedOut,
  };
};

export const usePulse = (options: UsePulseOptions): UsePulseResult =>
  usePulseResource(options);
